import { useState } from 'react'
import { Check } from 'lucide-react'
import type { QuestionProps } from '../eform.types'

export default function CheckboxOptions({ question, value, onChange, error }: QuestionProps) {
  const selected = Array.isArray(value) ? value : value ? value.split(',').map((s) => s.trim()) : []
  const [otherText, setOtherText] = useState('')

  function toggleOption(optId: string) {
    const next = selected.includes(optId)
      ? selected.filter((v) => v !== optId)
      : [...selected, optId]
    onChange(next)
  }

  function handleCheckAll() {
    const allIds = question.options.map((o) => String(o.id))
    const allSelected = allIds.every((id) => selected.includes(id))
    onChange(allSelected ? [] : allIds)
  }

  function toggleOther() {
    const otherKey = 'other:' + otherText
    if (selected.some((v) => v.startsWith('other:'))) {
      onChange(selected.filter((v) => !v.startsWith('other:')))
    } else {
      onChange([...selected, otherKey])
    }
  }

  function handleOtherText(text: string) {
    setOtherText(text)
    const withoutOther = selected.filter((v) => !v.startsWith('other:'))
    onChange([...withoutOther, 'other:' + text])
  }

  const hasOtherSelected = selected.some((v) => v.startsWith('other:'))

  return (
    <div className="space-y-1">
      {question.checkAll && (
        <button
          type="button"
          onClick={handleCheckAll}
          className="text-xs text-primary hover:underline mb-0.5"
        >
          {question.options.every((o) => selected.includes(String(o.id)))
            ? 'Deselect all'
            : 'Select all'}
        </button>
      )}
      {question.options.map((opt) => {
        const optVal = String(opt.id)
        const isChecked = selected.includes(optVal)
        return (
          <label
            key={opt.id}
            className={`flex items-center gap-2.5 rounded-md border px-3 py-2 cursor-pointer transition-all ${
              isChecked
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <div
              className={`h-4 w-4 rounded flex items-center justify-center shrink-0 transition-colors ${
                isChecked ? 'border-primary bg-primary border-2' : 'border-slate-300 border-2'
              }`}
            >
              {isChecked && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
            </div>
            <input
              type="checkbox"
              className="sr-only"
              checked={isChecked}
              onChange={() => toggleOption(optVal)}
            />
            <span className="text-xs text-slate-700">{opt.text}</span>
          </label>
        )
      })}
      {question.addOther && (
        <label
          className={`flex items-center gap-2.5 rounded-md border px-3 py-2 cursor-pointer transition-all ${
            hasOtherSelected
              ? 'border-primary bg-primary/5 ring-1 ring-primary'
              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
          }`}
        >
          <div
            className={`h-4 w-4 rounded flex items-center justify-center shrink-0 transition-colors ${
              hasOtherSelected ? 'border-primary bg-primary border-2' : 'border-slate-300 border-2'
            }`}
          >
            {hasOtherSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
          </div>
          <input
            type="checkbox"
            className="sr-only"
            checked={hasOtherSelected}
            onChange={toggleOther}
          />
          <div className="flex-1">
            <span className="text-xs text-slate-700">Other</span>
            {hasOtherSelected && (
              <input
                type="text"
                value={otherText}
                onChange={(e) => handleOtherText(e.target.value)}
                placeholder="Please specify"
                className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                autoFocus
              />
            )}
          </div>
        </label>
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}
